import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Button from 'react-bootstrap/Button';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
  Stack,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress, { circularProgressClasses } from '@mui/material/CircularProgress';
import { useSearchParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useIsAuthenticated } from '@azure/msal-react';
import { useSelector } from 'react-redux';
import api from '../../../api/apiSettings';
import { usePrevious, useWindowSize } from '../../../utils/hooks';
import classes from './jobsFilter.module.scss';
import ValidationErrorLabel from '../ValidationErrorLabel/ValidationErrorLabel';
import FilterChips from './components/FilterChips/FilterChips';
import FiltersMultiSelect from './components/FiltersSelect/FiltersMultiSelect';
import colors from '../../../scss/colors';
import JobAlerts from './JobAlerts';
import { MOBILE_BREAKPOINT } from '../../../utils/constants';

const StyledFieldContainer = styled('div')({
  display: 'inline-flex',
  width: '300px',
  verticalAlign: 'top',
  border: `1px solid ${colors.LIGHT_GREY}`,
  alignItems: 'center',
  padding: '10px',
  svg: {
    fontSize: '18px',
    color: '#d1d3d4'
  },
  input: {
    fontSize: '12px',
    lineHeight: '14px',
    border: 'none',
    padding: 0,
    marginRight: '6px'
  },
  '&:first-of-type': {
    borderRadius: '5px 0px 0px 5px'
  },
  '@media only screen and (max-width: 997px)': {
    width: '100%',
    borderRadius: '10px',
    '&:first-of-type': {
      borderRadius: '10px'
    }
  }
});

const autoCompleteStyles = {
  width: '300px',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  '.MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: 0
  },
  '.MuiFormControl-root': {
    padding: 0
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '0',
    padding: '0'
  },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
};

function JobsFilter(props) {
  const {
    changeHandler,
    stringChangeHandler,
    submitSearch,
    handleReset,
    resetTrigger,
    values,
    fieldsValues,
    searchLengthValidationError
  } = props;

  const fetchLocationSuggestions = (search) => api.get(`v1/locations?keyword=${search}`).then((res) => res?.data?.Data);

  const initLoadingText = 'Please type first 3 letters of your location';
  const functionsDirections = useSelector((state) => state.constFromApi.functionsDirections);
  const industries = useSelector((state) => state.constFromApi.industries);
  const contractTypes = useSelector((state) => state.constFromApi.contractTypes);
  const filterPeriods = useSelector((state) => state.constFromApi.filterPeriods);
  const [autoCompleteLocations, setAutoCompleteLocations] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [waitingForLocations, setWaitingForLocations] = useState(false);
  const [loadingTextForLocations, setLoadingTextForLocations] = useState(initLoadingText);
  const [textQuery, setTextQuery] = useState('');
  const prevTextQuery = usePrevious(textQuery);
  const [timerId, setTimer] = useState(null);
  const { t } = useTranslation();

  const screenWidth = useWindowSize().width;

  const selectFilters = [
    { name: 'function', label: t('dashboard.jobs.function'), options: functionsDirections },
    { name: 'industry', label: t('dashboard.jobs.industry'), options: industries },
    { name: 'contract_type', label: t('dashboard.jobs.type'), options: contractTypes },
    { name: 'date_posted', label: t('dashboard.jobs.date'), options: filterPeriods }
  ];

  const withDebounce = (func, delay) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    const timerDelayId = setTimeout(() => {
      setTimer(null);
      func();
    }, delay);
    setTimer(timerDelayId);
  };

  const [selectedFilter, setSelectedFilter] = useState(values);

  const resetFilters = () => {
    setSearchParams();
    setSelectedFilter({
      function: [],
      industry: [],
      contract_type: [],
      date_posted: []
    });
    setAutoCompleteLocations([]);
    handleReset();
  };

  useEffect(() => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...selectedFilter
    });

    if (resetTrigger) {
      resetFilters();
    }
    // }
  }, [selectedFilter, searchParams, resetTrigger]);

  const composableChangeHandler = (e, prop) => {
    if (['location', 'field'].includes(prop)) {
      stringChangeHandler(e, prop);
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        ...selectedFilter,
        [prop]: e.target.value
      });
    } else {
      changeHandler(e, prop);
    }
  };

  const onInputHandler = (e) => {
    if (!e.target.value) {
      setWaitingForLocations(false);
      setAutoCompleteLocations([]);
      return;
    }
    if (e.target.value.length > 2) {
      setLoadingTextForLocations('Loading...');
      setTextQuery(e.target.value);
      if (e.target.value !== prevTextQuery && e.target.value.includes(prevTextQuery) && autoCompleteLocations.length) {
        setTimeout(() => {
          const newAutocomplite = autoCompleteLocations.filter((item) =>
            item.label.toLowerCase().startsWith(e.target.value.toLowerCase())
          );
          setAutoCompleteLocations(newAutocomplite);
          setWaitingForLocations(false);
        }, 0);
      } else {
        fetchLocationSuggestions(e.target.value).then((res) => {
          setAutoCompleteLocations(res.map((y) => ({ label: y.CityName, id: y.Id })));
          setWaitingForLocations(false);
        });
      }
    } else {
      setLoadingTextForLocations(initLoadingText);
      setAutoCompleteLocations([]);
    }
    setWaitingForLocations(true);
  };

  const isAuthenticated = useIsAuthenticated();
  const [accordionState, setAccordionState] = useState(true);

  const multiSelectRender = () => {
    return (
      <>
        {selectFilters.map((filter, index) => (
          <FiltersMultiSelect
            key={filter.name}
            options={selectFilters[index]}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            changeHandler={(e) => composableChangeHandler(e, filter.name)}
            clearHandler={() => composableChangeHandler({ target: { value: '' } }, filter.name)}
          />
        ))}
        {/* {isAuthenticated ? <JobAlerts filters={values} /> : <div />} */}
        <a role="presentation" onClick={resetFilters} className={`d-inline-block px-3 ${classes.reset_button}`}>
          <small>{t('dashboard.jobs.reset')}</small>
        </a>
      </>
    );
  };

  return (
    <div>
      <div className="row pt-4">
        {!!searchLengthValidationError && <ValidationErrorLabel message="Please type first 3 letters of your search" />}
        <div className="col-lg-12">
          <div className={classes.searchBar}>
            <StyledFieldContainer className="mb-3">
              <SearchIcon />
              <OutlinedInput
                sx={{
                  width: '100%',
                  height: '100%',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
                placeholder={t('dashboard.jobs.search')}
                value={fieldsValues?.field}
                onChange={(e) => composableChangeHandler(e, 'field')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    submitSearch();
                  }
                }}
                endAdornment={
                  <InputAdornment position="end">
                    {fieldsValues?.field && (
                      <div
                        role="presentation"
                        className={classes.clearBtn}
                        onClick={() => composableChangeHandler({ target: { value: '' } }, 'field')}
                      >
                        <CloseIcon fontSize="10px" />
                      </div>
                    )}
                  </InputAdornment>
                }
              />
            </StyledFieldContainer>
            <StyledFieldContainer className="mb-3">
              <LocationOnIcon />
              <Autocomplete
                freeSolo
                loading={waitingForLocations}
                loadingText={loadingTextForLocations}
                clearOnBlur
                clearIcon={<CloseIcon fontSize="10px" />}
                disablePortal
                id="jobs-filter-location"
                options={autoCompleteLocations}
                // eslint-disable-next-line
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.label === value}
                value={fieldsValues?.location}
                onBlur={() => setAutoCompleteLocations([])}
                onChange={(e, val) => composableChangeHandler({ target: { value: val ? val.label : '' } }, 'location')}
                onInput={(e) => withDebounce(() => onInputHandler(e), 400)}
                sx={autoCompleteStyles}
                renderInput={(params) => (
                  <TextField
                    sx={{ paddingLeft: '30px' }}
                    {...params}
                    placeholder={t('dashboard.jobs.location')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.inputProps.value.length > 2 && waitingForLocations ? (
                            <CircularProgress
                              variant="indeterminate"
                              disableShrink
                              sx={{
                                animationDuration: '950ms',
                                position: 'absolute',
                                right: 0,
                                [`& .${circularProgressClasses.svg}`]: {
                                  left: 0,
                                  top: 0
                                }
                              }}
                              size={25}
                              thickness={4}
                              {...props}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </StyledFieldContainer>
            <Button type="submit" onClick={submitSearch} className={classes.search}>
              {t('dashboard.jobs.searchjob')}
            </Button>
          </div>
        </div>
      </div>

      {screenWidth <= MOBILE_BREAKPOINT ? (
        <Accordion
          disableGutters
          className={`${classes.accordionParent} mt-1`}
          sx={{
            '&:before': {
              display: 'none'
            }
          }}
          onChange={() => setAccordionState(!accordionState)}
        >
          <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
            <Stack direction="row" alignItems="center" justifyContent="center" gap={1} width="100%">
              <Typography>Filters</Typography>
              <ExpandMoreIcon
                sx={{
                  transform: `rotate(${accordionState ? 180 : 0}deg)`,
                  transition: 'transform 0.3s ease-in-out'
                }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.filters}>{multiSelectRender()}</div>
          </AccordionDetails>
        </Accordion>
      ) : (
        <div className="d-flex flex-row flex-wrap gap-2 py-4">{multiSelectRender()}</div>
      )}
      <div className="">
        <FilterChips
          changeHandler={changeHandler}
          setSelectedFilter={setSelectedFilter}
          selectedFilter={selectedFilter}
        />
      </div>
    </div>
  );
}

export default JobsFilter;
